import type { TooltipProps } from "@radix-ui/react-tooltip"
import * as React from "react"
import { cn } from "../../lib/utils"
import { Icon } from "../Icon/Icon"
import { Tooltip } from "../Tooltip/Tooltip"
import { Switch, type SwitchProps } from "../ui/switch"

export type ToggleProps = Omit<SwitchProps, "asChild"> & {
  /** Primary heading text */
  heading?: string | React.ReactNode
  subheading?: string
  /**
   * Position of the label relative to the switch.
   * If not provided but heading exists, defaults to "left".
   */
  labelPosition?: "left" | "right"
  /** If true, stretches the toggle+label to fill the full width of its container */
  fullWidth?: boolean
  headingToolTip?: string
  subheadingToolTip?: string
  headingToolTipProps?: TooltipProps
  subheadingToolTipProps?: TooltipProps
}

export const Toggle = React.forwardRef<HTMLButtonElement, ToggleProps>(
  (
    {
      heading,
      subheading,
      labelPosition = "left",
      id,
      checked,
      defaultChecked,
      onCheckedChange,
      disabled,
      className,
      fullWidth = false,
      headingToolTip,
      subheadingToolTip,
      headingToolTipProps,
      subheadingToolTipProps,
      variant = "default",
      ...props
    },
    ref
  ) => {
    const idHook = React.useId()
    const switchId = id || idHook
    const descriptionId = React.useId()
    const hasLabel = Boolean(heading || subheading)
    const hasBothLabels = Boolean(heading && subheading)
    const effectiveLabelPosition = hasLabel ? labelPosition : undefined

    const labelContent = hasLabel ? (
      <div
        className={cn(
          "flex",
          variant === "inverse" ? "text-root-inverse-text-base" : "text-root-text-color-base",
          hasBothLabels ? "flex-col space-y-1" : "items-center",
          disabled && "cursor-not-allowed opacity-50"
        )}
      >
        {heading && (
          <div className="flex items-center gap-1">
            <label htmlFor={switchId} className={cn("text-16 font-medium leading-6")}>
              {heading}
            </label>
            {headingToolTip && (
              <Tooltip content={headingToolTip} {...headingToolTipProps}>
                <Icon
                  name="Info"
                  className={cn(
                    "text-root-size-body-sm",
                    variant === "inverse"
                      ? "stroke-root-inverse-text-subtle"
                      : "stroke-root-icon-color-subtle"
                  )}
                />
              </Tooltip>
            )}
          </div>
        )}
        {subheading && (
          <div className="flex items-center gap-1">
            <p
              id={descriptionId}
              className={cn(
                "text-14 font-regular leading-5",
                variant === "inverse"
                  ? "text-root-inverse-text-subtle"
                  : "text-root-text-color-subtle"
              )}
            >
              {subheading}
            </p>
            {subheadingToolTip && (
              <Tooltip content={subheadingToolTip} {...subheadingToolTipProps}>
                <Icon
                  name="Info"
                  className={cn(
                    "w-icon-sm h-icon-sm",
                    variant === "inverse"
                      ? "stroke-root-inverse-text-subtle"
                      : "stroke-root-icon-color-subtle"
                  )}
                />
              </Tooltip>
            )}
          </div>
        )}
      </div>
    ) : null

    return (
      <div
        className={cn(
          "flex items-center flex-row",
          fullWidth && "w-full justify-between",
          hasLabel && "gap-2",
          className
        )}
      >
        {effectiveLabelPosition === "left" && labelContent}

        <Switch
          ref={ref}
          id={switchId}
          checked={checked}
          defaultChecked={defaultChecked}
          onCheckedChange={onCheckedChange}
          disabled={disabled}
          variant={variant}
          aria-describedby={subheading ? descriptionId : undefined}
          {...props}
        />

        {effectiveLabelPosition === "right" && labelContent}
      </div>
    )
  }
)

Toggle.displayName = "Toggle"
